import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCategoryClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-file-list-3-line text-lg text-background-50"></i>
            </div>
            <span className="font-heading text-xl text-foreground-900">
              Hipobuy Sheet
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <a
              href="#categories"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Categories
            </a>
            <a
              href="#about"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              About
            </a>
            <a
              href="#compare"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Compare
            </a>
            <Link
              to="/hipobuy-spreadsheet-guide"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-sm"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-background-200/60 py-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
            <div className="flex items-center gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-shopping-cart-line"></i>
                Buy It Now
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-list-3-line"></i>
                Hipobuy Sheet
              </a>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/hipobuy-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                Complete Guide
              </Link>
              <Link
                to="/hipobuy-spreadsheet-beginners"
                onClick={() => setMobileOpen(false)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-accent-200 text-accent-700 text-sm font-medium hover:bg-accent-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-smile-line"></i>
                Beginners
              </Link>
            </div>

            <div className="border-t border-background-200/60 pt-4">
              <div className="flex items-center gap-2 mb-3 px-1">
                <i className="ri-grid-line text-xs text-foreground-400"></i>
                <span className="text-xs font-semibold text-foreground-400 uppercase tracking-wide">Shop Categories</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleCategoryClick(cat.url)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 border border-background-200/60 hover:border-primary-200 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className={`${cat.icon} text-xs`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-700 leading-tight">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-3 flex flex-col gap-1">
              <Link
                to="/spreadsheet-platform-comparison"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors cursor-pointer flex items-center gap-2"
              >
                <i className="ri-scales-line text-accent-500"></i>
                Platform Comparison
              </Link>
              <Link
                to="/hipobuy-spreadsheet-safety"
                onClick={() => setMobileOpen(false)}
                className="px-3 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-md transition-colors cursor-pointer flex items-center gap-2"
              >
                <i className="ri-shield-check-line text-secondary-500"></i>
                Safety Guide
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}